"use client";

import { useEffect } from "react";
import { Starfield } from "@/components/Starfield";
// global-error REPLACES app/layout.tsx when it fires, so none of that layout's
// <link> tags — this stylesheet included — ship with it. Import it directly or
// the page renders as unstyled black-on-white text.
import "./globals.css";
import { fraunces, spaceGrotesk, spaceMono } from "./fonts";

// app/layout.tsx puts these three `.variable` classNames on <html>, and they are
// what make globals.css's `--font-fraunces` / `--font-space-grotesk` /
// `--font-space-mono` resolve. This file renders its own <html>, so it hangs all
// three on that <html> too — otherwise the serif heading and every mono label here
// fall back to a system face while the rest of the app looks right. The faces are
// declared once, in app/fonts.ts, and imported above.

/**
 * Root-layout error boundary. Fires when app/layout.tsx itself throws, or when a
 * throw elsewhere has no error.tsx boundary above it to land in. It must define
 * its own <html>/<body>: it replaces the root layout entirely, not just the
 * segment beneath it.
 *
 * Styled from the app's own surfaces rather than inline hex, so it stays in step
 * with globals.css: `.field` + <Starfield /> paint the night sky, and the copy
 * sits in the same `.auth-card` the sign-in page uses.
 */
export default function GlobalError({
  error,
  reset,
}: {
  error: Error & { digest?: string };
  reset: () => void;
}) {
  useEffect(() => {
    void (async () => {
      try {
        const { reportError } = await import("@/lib/flare");
        await reportError(error, { kind: "client", digest: error.digest });
      } catch {
        // The page is already broken; a failed report must not make it worse.
      }
    })();
  }, [error]);

  return (
    <html lang="en" className={`${fraunces.variable} ${spaceGrotesk.variable} ${spaceMono.variable}`}>
      <head>
        {/* Next's global-error path bypasses metadata/viewport resolution entirely
            (its own DefaultGlobalError has the same gap), so without this the
            installed iOS PWA renders this page at desktop width — exactly the
            scenario the file exists for. */}
        <meta name="viewport" content="width=device-width, initial-scale=1, viewport-fit=cover" />
      </head>
      <body>
        <div className="field" />
        <Starfield />
        <main className="auth">
          <div className="auth-card">
            <div className="mark"><span className="star">✦</span><b>NEXUS</b></div>
            {/* `.auth-card p` caps the measure at 30ch, which leaves this copy with a
                one-word last line. `text-wrap: balance` is the app's own answer to that
                (see `.hero h1` in globals.css); nothing sets it on these two elements. */}
            <h1 style={{ textWrap: "balance" }}>Nexus hit an error.</h1>
            <p style={{ textWrap: "balance" }}>The problem was reported automatically. Trying again usually works.</p>
            {/* `.new` is the app's primary button, but at 0.7rem mono it measures ~36px;
                the fleet's 44px tap floor is the one thing globals.css does not give it here. */}
            <button
              type="button"
              className="new"
              onClick={() => reset()}
              style={{ width: "100%", justifyContent: "center", minHeight: 44 }}
            >
              Try again
            </button>
          </div>
        </main>
      </body>
    </html>
  );
}
